import type { MerchantDraftData, WorkspaceRiskCatalogItem, WorkspaceRiskListItem } from "@unbox/api";

export type RiskSelection = NonNullable<MerchantDraftData["detection"]>["risks"][number];
export type RiskHandling = NonNullable<MerchantDraftData["handling"]>["risks"][number];
export type StartingPoint = NonNullable<MerchantDraftData["detection"]>["startingPoint"];

type CatalogRisk = Pick<
  WorkspaceRiskCatalogItem,
  "id" | "category" | "defaultTier" | "defaultEnabled" | "defaultAutoConfirm" | "defaultSafetyPolicyType"
>;
type WorkspaceRisk = Pick<WorkspaceRiskListItem, "riskId" | "tier" | "autoConfirm" | "safetyPolicyType">;

export const isCoreSafety = (risk: Pick<CatalogRisk, "category">) => risk.category === "baseline";

export const startingRisks = (
  catalog: CatalogRisk[],
  workspaceRisks: WorkspaceRisk[],
  startingPoint: StartingPoint,
): RiskSelection[] => {
  const workspaceById = new Map(workspaceRisks.map((risk) => [risk.riskId, risk]));

  return catalog.flatMap((risk) => {
    const workspaceRisk = workspaceById.get(risk.id);
    const isOn = isCoreSafety(risk) || (startingPoint === "workspace" ? !!workspaceRisk : risk.defaultEnabled);
    if (!isOn) return [];
    const tier = startingPoint === "workspace" ? (workspaceRisk?.tier ?? risk.defaultTier) : risk.defaultTier;
    return [{ riskId: risk.id, tier }];
  });
};

export const handlingFor = (
  catalog: CatalogRisk[],
  workspaceRisks: WorkspaceRisk[],
  selection: RiskSelection[],
  existing: RiskHandling[] = [],
): RiskHandling[] => {
  const catalogById = new Map(catalog.map((risk) => [risk.id, risk]));
  const workspaceById = new Map(workspaceRisks.map((risk) => [risk.riskId, risk]));
  const existingById = new Map(existing.map((risk) => [risk.riskId, risk]));

  return selection.map(({ riskId }) => {
    const kept = existingById.get(riskId);
    if (kept) return kept;
    const workspaceRisk = workspaceById.get(riskId);
    if (workspaceRisk) {
      return { riskId, autoConfirm: workspaceRisk.autoConfirm, safetyPolicyType: workspaceRisk.safetyPolicyType };
    }
    const risk = catalogById.get(riskId);
    return {
      riskId,
      autoConfirm: risk?.defaultAutoConfirm ?? false,
      safetyPolicyType: risk?.defaultSafetyPolicyType ?? null,
    };
  });
};

export const skippedHandling = (catalog: CatalogRisk[], selection: RiskSelection[]): RiskHandling[] => {
  const catalogById = new Map(catalog.map((risk) => [risk.id, risk]));
  return selection.map(({ riskId }) => ({
    riskId,
    autoConfirm: false,
    safetyPolicyType: catalogById.get(riskId)?.defaultSafetyPolicyType ?? null,
  }));
};
